import { getScrollBlockBottomPadding } from "../../Preview/previewPlaybackUtils";
import { layoutTitles } from "./blocks/heading";
import { layoutBlurb } from "./blocks/blurb";
import { layoutNameOnly } from "./blocks/nameOnly";
import { layoutRoleName } from "./blocks/roleName";
import { layoutSong } from "./blocks/song";
import { layoutLogo } from "./blocks/logo";
import { toPx } from "./text";

export const CANONICAL_PREVIEW_WIDTH = 800;
// `extra_lines_below` is N × 1em at the inherited 16px root font size.
const EXTRA_LINE_PX = 16;
// BlurbBlock / NameOnlyBlock2 / SongBlock2 / LogoBlock use Tailwind px-1.
const BLOCK_SIDE_PADDING = 4;

const BODY_LAYOUTS = {
  blurb: layoutBlurb,
  nameonly: layoutNameOnly,
  rolename: layoutRoleName,
  song: layoutSong,
  logo: layoutLogo,
};

const getInsetRegion = (region, inset) => {
  if (inset === "left") return { left: region.left, width: region.width / 2 };
  if (inset === "right") {
    return { left: region.left + region.width / 2, width: region.width / 2 };
  }
  return { left: region.left, width: region.width };
};

/** Block.js content (titles + body) at `top`; returns its height. */
function layoutBlockContent(credit, idx, { left, width, top }, env) {
  const options = credit?.options || {};
  const type = options.block_type;
  const body = BODY_LAYOUTS[type];
  if (!body) return 0;
  if (!options.text_alignment && type !== "logo") return 0;

  const pad = type === "rolename" ? 0 : BLOCK_SIDE_PADDING;
  const ctx = {
    ...env,
    options,
    items: credit?.items || [],
    blockIndex: idx,
    left: left + pad,
    width: Math.max(0, width - pad * 2),
    top,
  };
  const titlesHeight = layoutTitles(ctx);
  const bodyHeight = body({ ...ctx, top: top + titlesHeight });
  return titlesHeight + bodyHeight;
}

/** One `.viewBlock`: padding, content and extra lines below. */
export function layoutCreditBox(credit, idx, region, env) {
  const options = credit?.options || {};
  const inset = getInsetRegion(region, options.block_inset);
  const paddingTop = toPx(options.margin_vertical, 0);
  const paddingBottom = getScrollBlockBottomPadding({
    blockType: options.block_type,
    marginVertical: options.margin_vertical,
    isCard: Boolean(env?.isCard),
  });
  const ops = [];
  const hits = [];
  const contentHeight = layoutBlockContent(
    credit,
    idx,
    { left: inset.left, width: inset.width, top: region.top + paddingTop },
    { ...env, ops, hits },
  );
  const extraLines = Math.max(0, Number(options.extra_lines_below) || 0);
  const height =
    paddingTop + contentHeight + extraLines * EXTRA_LINE_PX + paddingBottom;
  return {
    idx,
    id: credit?._id,
    x: inset.left,
    top: region.top,
    width: inset.width,
    height,
    ops,
    hits,
  };
}

/**
 * Build the full scroll scene: a lead-in blank, one wrapper per
 * `.blockWrapper` (side-by-side pairs share one), and a tail blank.
 *
 * env: { measurer, fontStyle, settings, images }
 */
export function layoutScene({
  credits = [],
  settings,
  fontStyle,
  measurer,
  images,
  width = CANONICAL_PREVIEW_WIDTH,
  blankHeight = 0,
}) {
  const env = { measurer, fontStyle: fontStyle || {}, settings: settings || {}, images };
  const wrappers = [];
  let y = blankHeight;

  (credits || []).forEach((credit, idx) => {
    const inset = credit?.options?.block_inset;
    if (inset === "sidebyside") {
      const columnWidth = width / 2;
      const boxes = [
        layoutCreditBox(credit, idx, { left: 0, width: columnWidth, top: y }, env),
      ];
      if (credits[idx + 1]) {
        boxes.push(
          layoutCreditBox(
            credits[idx + 1],
            idx + 1,
            { left: columnWidth, width: columnWidth, top: y },
            env,
          ),
        );
      }
      const height = Math.max(...boxes.map((box) => box.height));
      wrappers.push({
        idx,
        top: y,
        height,
        credits: boxes.map((box, i) => ({
          ...box,
          focus: { x: i * columnWidth, y, width: columnWidth, height },
        })),
      });
      y += height;
      return;
    }
    if (credits[idx - 1]?.options?.block_inset === "sidebyside") return;

    const box = layoutCreditBox(credit, idx, { left: 0, width, top: y }, env);
    wrappers.push({
      idx,
      top: y,
      height: box.height,
      credits: [{ ...box, focus: { x: 0, y, width, height: box.height } }],
    });
    y += box.height;
  });

  return {
    width,
    blankHeight,
    contentTop: blankHeight,
    contentBottom: y,
    height: y + blankHeight,
    wrappers,
  };
}

/** Credit under a canonical-space point, preferring the innermost box. */
export function hitTestScene(scene, x, y) {
  if (!scene) return null;
  for (const wrapper of scene.wrappers) {
    if (y < wrapper.top || y >= wrapper.top + wrapper.height) continue;
    for (const credit of wrapper.credits) {
      if (
        x >= credit.x &&
        x < credit.x + credit.width &&
        y >= credit.top &&
        y < credit.top + credit.height
      ) {
        const logo = credit.hits.find(
          (hit) =>
            x >= hit.x && x < hit.x + hit.width && y >= hit.y && y < hit.y + hit.height,
        );
        return { wrapper, credit, logo: logo || null };
      }
    }
    return { wrapper, credit: null, logo: null };
  }
  return null;
}
